import { useState, useRef, useEffect } from 'react';
import { ChatCircleDots, PaperPlaneRight, X } from '@phosphor-icons/react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TF_LOGO = "https://www.targetfunder.com/tf_logo.png";

export function HelpChat({ pageContext = '', clubSlug = '', embedMode = false }) {
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === 'undefined' || embedMode) return false;
    return sessionStorage.getItem('tf-help-chat-dismissed') === '1';
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `chat-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const handleDismiss = (e) => {
    e.stopPropagation();
    sessionStorage.setItem('tf-help-chat-dismissed', '1');
    setDismissed(true);
  };

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: "Hi! I'm TargetFunder's help assistant. Ask me anything about the World Cup 2026 Predictor — whether you're a club looking to set up a fundraiser, or an entrant with questions about the competition."
      }]);
    }
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
    // Notify parent window when used inside an iframe embed
    if (embedMode && typeof window !== 'undefined' && window.parent !== window) {
      window.parent.postMessage({ type: 'tf-chat', open }, '*');
    }
  }, [open, embedMode]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    const msg = input.trim();
    if (!msg || loading) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/chat`, {
        message: msg,
        session_id: sessionId,
        page_context: pageContext,
        club_slug: clubSlug,
      });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Sorry, I'm having trouble right now. Please email support@targetfunder.com for help."
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Chat bubble — pushed up on mobile when the ClubPredictorPage mobile
          selection bar is visible. Body class `has-mobile-bar` is toggled by
          ClubPredictorPage so the bubble doesn't cover the CTA on club pages. */}
      {!open && !dismissed && (
        <div className="help-chat-bubble fixed right-4 lg:right-6 bottom-6 z-[70]">
          <button
            onClick={() => setOpen(true)}
            className="w-12 h-12 lg:w-14 lg:h-14 bg-[#1D3557] hover:bg-[#15293f] text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-105"
            data-testid="help-chat-bubble"
            aria-label="Open help chat"
          >
            <ChatCircleDots size={24} weight="fill" className="lg:hidden" />
            <ChatCircleDots size={28} weight="fill" className="hidden lg:block" />
          </button>
          {/* Mobile-only dismiss badge — hides the bubble for the session */}
          <button
            onClick={handleDismiss}
            className="lg:hidden absolute -top-1.5 -right-1.5 w-5 h-5 bg-white text-[#1D3557] border border-gray-200 rounded-full shadow flex items-center justify-center"
            data-testid="help-chat-dismiss"
            aria-label="Hide help chat"
          >
            <X size={11} weight="bold" />
          </button>
        </div>
      )}

      {/* Chat window — also shifts above the mobile bar on club pages. */}
      {open && !dismissed && (
        <div className="help-chat-window fixed right-4 lg:right-6 bottom-6 z-[70] w-[340px] sm:w-[360px] max-w-[calc(100vw-32px)] h-[460px] max-h-[calc(100vh-120px)] bg-white border border-gray-200 shadow-2xl flex flex-col" style={{ borderRadius: '12px' }} data-testid="help-chat-window">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-[#1D3557] text-white" style={{ borderRadius: '12px 12px 0 0' }}>
            <div className="flex items-center gap-2.5">
              <img src={TF_LOGO} alt="TF" className="w-7 h-7 rounded-full object-cover bg-white" />
              <div>
                <p className="text-sm font-bold leading-tight">TargetFunder Help</p>
                <p className="text-[10px] text-white/50">Powered by AI</p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-white/60 hover:text-white transition-colors"
              data-testid="help-chat-close"
            >
              <X size={20} weight="bold" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-gray-50">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-[#1D3557] text-white rounded-t-xl rounded-bl-xl'
                      : 'bg-white border border-gray-200 text-gray-700 rounded-t-xl rounded-br-xl'
                  }`}
                  style={{ whiteSpace: 'pre-wrap' }}
                >
                  {msg.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 rounded-t-xl rounded-br-xl px-4 py-3 flex gap-1.5">
                  <div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="px-3 py-3 border-t border-gray-200 bg-white" style={{ borderRadius: '0 0 12px 12px' }}>
            <form
              onSubmit={(e) => { e.preventDefault(); handleSend(); }}
              className="flex items-center gap-2"
            >
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                placeholder="Ask a question..."
                className="flex-1 bg-gray-50 border border-gray-200 text-gray-800 px-3.5 py-2.5 text-sm rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1D3557] focus:border-[#1D3557] placeholder:text-gray-400"
                disabled={loading}
                data-testid="help-chat-input"
              />
              <button
                type="submit"
                disabled={!input.trim() || loading}
                className="w-10 h-10 bg-[#E63946] hover:bg-[#d32f3c] text-white rounded-lg flex items-center justify-center transition-colors disabled:opacity-30"
                data-testid="help-chat-send"
              >
                <PaperPlaneRight size={18} weight="fill" />
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
